import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BrandSitesV2Theme } from '../common/GlobalStyle';
import useBreakpoints from '../hooks/useBreakpoints';

export default function StorySection() {
  const media = useBreakpoints({ defaultMedia: 'desktop' });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [imageWidth, setImageWidth] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end end'],
  });

  const scale = useSpring(useTransform(scrollYProgress, [0, 0.8], [1, 1.1]), {
    stiffness: 600,
    damping: 100,
  });

  const calcContainerPadding = (_media: typeof media) =>
    (document.body.clientWidth - Number(BrandSitesV2Theme!.brandSitesV2.containerWidths[_media].replace('px', ''))) / 2;

  useEffect(() => {
    const calcImageWidth = () => {
      let paddingLeft = calcContainerPadding(media);

      if (media === 'mobile') {
        paddingLeft = Math.min(24, paddingLeft);
        paddingLeft = Math.max(calcContainerPadding('tablet'), paddingLeft);
      }

      let width = document.body.clientWidth - paddingLeft;
      setImageWidth(width);
    };

    calcImageWidth();
    window.addEventListener('resize', calcImageWidth);
    return () => {
      window.removeEventListener('resize', calcImageWidth);
    };
  }, [media]);

  return (
    <Container ref={containerRef}>
      <OuterWrapper>
        <InnerWrapper>
          <TitleWrapper>
            <FakeTitle>함께 만들어갈 청연과 그다음 이야기</FakeTitle>
            <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
            <TitleTablet>{TitleTabletSvg}</TitleTablet>
          </TitleWrapper>
          <Content>
            <Description>
              청소를 시작으로 청연팀과 함께 집과 우리 생활에 더 나은 변화를 만들어 나갈 멋진 동료를 기다리고 있습니다.
            </Description>
            <Link href="https://cleaninglab-official.notion.site/fab33235930c479598c3b88c3cb169ba" passHref>
              <Button target="_blank">채용공고 보러가기</Button>
            </Link>
          </Content>
        </InnerWrapper>
      </OuterWrapper>
      <ImageWrapper $width={imageWidth}>
        <picture>
          <source
            type="image/webp"
            srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/about/webp/story.webp"
          />
          <source
            type="image/png"
            srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/about/story.png"
          />
          <Image
            style={{ scale }}
            ref={imageRef}
            src="https://cleanhome-web.cleaninglab.co.kr/brandsite/about/story.png"
            alt=""
          />
        </picture>
      </ImageWrapper>
    </Container>
  );
}

const Container = styled.section`
  position: relative;
  width: 100%;
  background: #f6f0e6;
  overflow: hidden;
  padding-top: 80px;
  padding-bottom: 100px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 60px;
    padding-bottom: 80px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-top: 70px;
    padding-bottom: 50px;
  }
`;

const OuterWrapper = styled.div`
  position: relative;
  padding: 0 24px;
  margin-bottom: 80px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-bottom: 60px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 52px;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 auto;

  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.mobile}; */
    flex-direction: column;
  }
`;

const TitleWrapper = styled.div`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 14px;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }
`;

const Content = styled.div``;

const Description = styled.h2`
  word-break: keep-all;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  margin-bottom: 40px;
  width: 502px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;
    margin-bottom: 30px;
    width: 405px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 44px;
    width: auto;
    font-weight: 400;
    word-break: break-all;
  }
`;

const Button = styled.a`
  display: inline-block;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  border: 1px solid #999999;
  border-radius: 8px;
  padding: 12px 20px;
  transition: 0.2s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 10px 16px;
    font-size: 15px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 9px 16px;
  }

  &:hover {
    color: #ffffff;
    background: #42484c;
    border-color: #42484c;
  }
`;

const ImageWrapper = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  height: 440px;
  overflow: hidden;
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    height: 340px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    height: ${({ $width }) => Math.round(Math.max($width / 2, 200))}px;
  }
`;

const Image = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 0%;
`;

const TitleDesktopSvg = (
  <svg width="397" height="105" viewBox="0 0 397 105" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M25.266 11.2033V6.92529H0.794V11.2033H25.266ZM19.148 4.62529V0.301295H7.418V4.62529H19.148ZM17.952 20.2193C17.952 22.1973 16.158 23.7613 13.398 23.7613C10.592 23.7613 8.798 22.1973 8.798 20.2193C8.798 18.1493 10.592 16.6773 13.398 16.6773C16.158 16.6773 17.952 18.1493 17.952 20.2193ZM22.874 20.2193C22.874 15.3893 18.734 12.6293 13.398 12.6293C8.016 12.6293 3.692 15.3893 3.692 20.2193C3.692 24.8193 8.016 27.7173 13.398 27.7173C18.596 27.7173 22.874 24.8193 22.874 20.2193ZM29.084 39.8153H14.594V34.1573H29.084V39.8153ZM34.144 44.1853V29.7413H9.534V44.1853H34.144ZM41.09 16.1713V11.8013H34.144V0.439296H29.084V27.4413H34.144V16.1713H41.09ZM46.0084 27.6253C50.4244 22.4733 53.2764 13.4573 53.0464 3.2453H43.1564V7.4773H48.0324C48.0324 13.2733 45.6404 20.8633 41.8224 25.0493L46.0084 27.6253ZM74.7584 38.8953H79.6804V0.531295H74.7584V38.8953ZM55.9904 29.5113C58.5204 26.2453 60.3144 22.3353 61.3724 17.8733L61.6484 16.5393H66.6624V37.4233H71.5384V1.0833H66.6624V12.2613H62.3844C62.7524 9.3633 62.9364 6.3733 62.8904 3.2453H54.8404V7.4773H57.8764C57.8764 13.5033 56.1744 21.6913 51.6204 26.9813L55.9904 29.5113ZM130.105 42.7133V38.2973H109.681V27.5793H104.529V42.7133H130.105ZM111.889 18.7013H101.585V7.1093H111.889V18.7013ZM116.903 23.0713V2.7393H96.4334V23.0713H116.903ZM135.855 15.6653V11.2953H128.863V0.485295H123.757V30.7073H128.863V15.6653H135.855ZM170.398 44.4153V40.1833H148.088V37.1933H169.248V26.0153H143.12V30.1553H164.234V33.1453H143.12V44.4153H170.398ZM175.688 23.2093V18.8853H136.956V23.2093H175.688ZM169.478 15.5733V11.2953H148.456V5.40729H169.34V1.0373H143.35V15.5733H169.478ZM184.562 14.7913C184.562 10.3293 187.23 7.6153 190.91 7.6153C194.59 7.6153 197.35 10.3293 197.35 14.7913C197.35 19.2533 194.636 21.9673 190.91 21.9673C187.184 21.9673 184.562 19.2533 184.562 14.7913ZM179.364 14.8373C179.364 21.8753 184.24 26.6133 190.91 26.6133C195.924 26.6133 199.926 23.8993 201.674 19.6673C201.996 18.7933 202.272 17.8733 202.41 16.9073H209.54V38.8493H214.646V0.531295H209.54V12.6293H202.364C202.226 11.7553 201.996 10.8813 201.674 10.0533C199.972 5.68329 195.878 3.0153 190.91 3.0153C184.24 3.0153 179.364 7.79929 179.364 14.8373ZM221.543 2.23329V6.4653H235.481C233.963 12.9053 228.351 17.5053 219.381 19.9893L221.819 23.8993C231.755 21.1853 240.679 14.3313 241.277 2.23329H221.543ZM253.835 44.1393V39.7693H233.641V36.5033H253.145V24.4053H228.581V28.8213H248.039V32.1793H228.581V44.1393H253.835ZM260.091 13.6873V9.3633H253.145V0.439296H248.039V22.1973H253.145V13.6873H260.091ZM292.622 4.99329V0.623296H279.466V4.99329H292.622ZM303.708 35.5833C303.708 38.5273 301.132 40.3673 297.314 40.3673C293.634 40.3673 291.012 38.5273 291.012 35.5833C291.012 32.6393 293.634 30.7993 297.314 30.7993C301.132 30.7993 303.708 32.6393 303.708 35.5833ZM309.412 25.9233V0.393296H304.306V13.6413H297.038V17.9193H304.306V25.9233H309.412ZM283.284 11.9853C283.192 13.0893 282.962 14.0553 282.548 14.9293C280.984 18.4713 277.442 20.5873 273.486 22.1053L275.878 26.1533C280.432 24.8193 284.756 21.1393 286.182 17.8273C287.654 21.0933 292.3 24.0833 296.348 25.3253L298.556 21.1853C294.462 20.0813 291.058 17.9193 289.678 14.6533C289.356 13.8713 289.126 12.9513 289.034 11.9853H297.36V7.6613H274.682V11.9853H283.284ZM308.86 35.5833C308.86 30.2933 304.076 26.4753 297.314 26.4753C290.69 26.4753 285.86 30.2933 285.86 35.5833C285.86 40.9653 290.69 44.7373 297.314 44.7373C304.076 44.7373 308.86 40.9653 308.86 35.5833ZM345.656 6.28129H334.892C332.868 4.16529 329.924 2.8313 326.474 2.8313C319.942 2.8313 315.25 7.4773 315.25 13.4113C315.25 19.4833 319.942 23.9913 326.474 23.9913C330.154 23.9913 333.282 22.5653 335.26 20.1733H345.656V30.0633H350.808V0.485295H345.656V6.28129ZM351.406 42.7133V38.2973H330.614V27.5793H325.508V42.7133H351.406ZM332.638 13.3653C332.638 16.8613 329.97 19.5753 326.474 19.5753C322.886 19.5753 320.31 16.8613 320.31 13.3653C320.31 9.8693 322.886 7.2933 326.474 7.2933C329.97 7.2933 332.638 9.8693 332.638 13.3653ZM337.698 13.4113C337.698 12.5373 337.56 11.6173 337.376 10.7893H345.656V15.7573H337.468C337.606 15.0213 337.698 14.2393 337.698 13.4113ZM379.14 2.87729H357.428V7.38529H374.034C374.172 10.6513 373.804 16.7693 373.068 20.4953L377.898 21.1393C379.002 17.0453 379.14 9.91529 379.14 6.1433V2.87729ZM382.038 24.5893C379.554 24.8653 375.69 25.1873 371.78 25.4633C370.492 25.5553 369.25 25.6013 368.1 25.6473V14.3313H362.994V25.8313C362.396 25.7853 361.752 25.7853 361.108 25.8313C358.762 25.8313 356.508 25.7853 354.852 25.7853L355.404 30.3393H358.624C362.074 30.3393 365.754 30.2473 368.882 30.0633C373.39 29.8333 378.91 29.1893 382.36 28.6833L382.038 24.5893ZM396.252 18.5633V14.2853H389.766V0.531295H384.66V38.8953H389.766V18.5633H396.252ZM39.526 93.6513V89.2813H0.794V93.6513H39.526ZM28.578 67.3853V70.3293C28.578 75.8493 27.98 81.8753 27.106 85.6013L32.12 86.1533C33.086 81.1393 33.592 75.1133 33.73 68.5353V63.0153H6.452V67.3853H28.578ZM43.6164 87.9013C51.8044 88.2693 60.8664 87.8553 67.5364 86.6593L67.0304 82.3353C61.6024 83.4393 53.8284 83.5773 48.7684 83.3473V67.4773H63.2124V63.1073H43.6164V87.9013ZM75.4944 60.5313H70.3424V98.8493H75.4944V77.3673H82.4864V72.9973H75.4944V60.5313ZM109.807 69.8233C109.807 72.8133 107.047 75.0673 102.953 75.0673C98.7208 75.0673 96.0528 72.8133 96.0528 69.8233C96.0528 66.6493 98.9048 64.5333 102.953 64.5333C106.955 64.5333 109.807 66.6493 109.807 69.8233ZM114.959 69.8233C114.959 64.0733 109.761 60.2093 102.953 60.2093C96.2828 60.2093 90.9008 64.0273 90.9008 69.8233C90.9008 75.9413 96.5588 79.2533 102.953 79.2533C109.209 79.2533 114.959 75.8953 114.959 69.8233ZM110.957 99.8153H95.0408V93.1453H110.957V99.8153ZM116.017 104.185V88.8673H89.9348V104.185H116.017ZM122.319 85.6933V81.3693H83.5868V85.6933H122.319ZM138.014 74.8373C138.014 81.8753 142.89 86.6133 149.606 86.6133C156.276 86.6133 161.198 81.8753 161.198 74.8373C161.198 67.7993 156.276 63.0153 149.606 63.0153C142.89 63.0153 138.014 67.7993 138.014 74.8373ZM143.212 74.7913C143.212 70.3293 145.88 67.6153 149.606 67.6153C153.24 67.6153 156 70.3293 156 74.7913C156 79.2533 153.286 81.9673 149.606 81.9673C145.88 81.9673 143.212 79.2533 143.212 74.7913ZM167.638 98.8493H172.744V60.5313H167.638V98.8493ZM183.642 74.7913C183.642 70.2373 186.31 67.5233 189.99 67.5233C193.532 67.5233 196.43 70.2833 196.43 74.7913C196.43 79.3453 193.532 82.0593 189.99 82.0593C186.31 82.0593 183.642 79.3453 183.642 74.7913ZM218.648 83.0713V78.6093H211.702V72.4913H218.648V68.0753H211.702V60.5313H206.642V98.8493H211.702V83.0713H218.648ZM178.49 74.8373C178.49 81.9673 183.32 86.7513 189.99 86.7513C196.706 86.7513 201.582 81.9673 201.582 74.8373C201.582 67.7533 196.706 62.7853 189.99 62.7853C183.32 62.7853 178.49 67.7533 178.49 74.8373ZM223.153 63.1993V67.6613H237.781C237.183 74.9293 231.157 82.1053 221.083 85.7853L224.027 89.9713C230.053 87.5333 234.791 84.0833 238.195 79.6213C241.599 75.1593 243.301 69.6853 243.347 63.1993H223.153ZM250.339 98.8493H255.491V60.5313H250.339V98.8493Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="233" height="65" viewBox="0 0 233 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.417 6.988V4.477H0.053V6.988H14.417ZM10.826 3.127V0.588999H3.941V3.127H10.826ZM10.124 12.28C10.124 13.441 9.071 14.359 7.451 14.359C5.804 14.359 4.751 13.441 4.751 12.28C4.751 11.065 5.804 10.201 7.451 10.201C9.071 10.201 10.124 11.065 10.124 12.28ZM13.013 12.28C13.013 9.445 10.583 7.825 7.451 7.825C4.292 7.825 1.754 9.445 1.754 12.28C1.754 14.98 4.292 16.681 7.451 16.681C10.502 16.681 13.013 14.98 13.013 12.28ZM16.658 23.782H8.153V20.461H16.658V23.782ZM19.628 26.347V17.869H5.183V26.347H19.628ZM23.705 9.904V7.339H19.628V0.669999H16.658V16.519H19.628V9.904H23.705ZM26.5919 16.627C29.1839 13.603 30.8579 8.311 30.7229 2.317H24.9179V4.801H27.7799C27.7799 8.203 26.3759 12.658 24.1349 15.115L26.5919 16.627ZM43.4669 23.242H46.3559V0.723999H43.4669V23.242ZM32.4509 17.734C33.9359 15.817 34.9889 13.522 35.6099 10.903L35.7719 10.12H38.7149V22.378H41.5769V1.048H38.7149V7.609H36.2039C36.4199 5.908 36.5279 4.153 36.5009 2.317H31.7759V4.801H33.5579C33.5579 8.338 32.5589 13.144 29.8859 16.249L32.4509 17.734ZM75.9532 25.483V22.891H63.9652V16.6H60.9412V25.483H75.9532ZM65.2612 11.389H59.2132V4.585H65.2612V11.389ZM68.2042 13.954V2.02H56.1892V13.954H68.2042ZM79.3282 9.607V7.042H75.2242V0.696999H72.2272V18.436H75.2242V9.607H79.3282ZM99.6031 26.482V23.998H86.5081V22.243H98.9281V15.682H83.5921V18.112H95.9851V19.867H83.5921V26.482H99.6031ZM102.708 14.035V11.497H79.9741V14.035H102.708ZM99.0631 9.553V7.042H86.7241V3.586H98.9821V1.021H83.7271V9.553H99.0631ZM107.917 9.094C107.917 6.475 109.483 4.882 111.643 4.882C113.803 4.882 115.423 6.475 115.423 9.094C115.423 11.713 113.83 13.306 111.643 13.306C109.456 13.306 107.917 11.713 107.917 9.094ZM104.866 9.121C104.866 13.252 107.728 16.033 111.643 16.033C114.586 16.033 116.935 14.44 117.961 11.956C118.15 11.443 118.312 10.903 118.393 10.336H122.578V23.215H125.575V0.723999H122.578V7.825H118.366C118.285 7.312 118.15 6.799 117.961 6.313C116.962 3.748 114.559 2.182 111.643 2.182C107.728 2.182 104.866 4.99 104.866 9.121ZM129.623 1.723V4.207H137.804C136.913 7.987 133.619 10.687 128.354 12.145L129.785 14.44C135.617 12.847 140.855 8.824 141.206 1.723H129.623ZM148.577 26.32V23.755H136.724V21.838H148.172V14.737H133.754V17.329H145.175V19.3H133.754V26.32H148.577ZM152.249 8.446V5.908H148.172V0.669999H145.175V13.441H148.172V8.446H152.249ZM171.343 3.343V0.778H163.621V3.343H171.343ZM177.85 21.298C177.85 23.026 176.338 24.106 174.097 24.106C171.937 24.106 170.398 23.026 170.398 21.298C170.398 19.57 171.937 18.49 174.097 18.49C176.338 18.49 177.85 19.57 177.85 21.298ZM181.198 15.628V0.643H178.201V8.419H173.935V10.93H178.201V15.628H181.198ZM165.862 7.447C165.808 8.095 165.673 8.662 165.43 9.175C164.512 11.254 162.433 12.496 160.111 13.387L161.515 15.763C164.188 14.98 166.726 12.82 167.563 10.876C168.427 12.793 171.154 14.548 173.53 15.277L174.826 12.847C172.423 12.199 170.425 10.93 169.615 9.013C169.426 8.554 169.291 8.014 169.237 7.447H174.124V4.909H160.813V7.447H165.862ZM180.874 21.298C180.874 18.193 178.066 15.952 174.097 15.952C170.209 15.952 167.374 18.193 167.374 21.298C167.374 24.457 170.209 26.671 174.097 26.671C178.066 26.671 180.874 24.457 180.874 21.298ZM202.472 4.099H196.154C194.966 2.857 193.238 2.074 191.213 2.074C187.379 2.074 184.625 4.801 184.625 8.284C184.625 11.848 187.379 14.494 191.213 14.494C193.373 14.494 195.209 13.657 196.37 12.253H202.472V18.058H205.496V0.696999H202.472V4.099ZM205.847 25.483V22.891H193.643V16.6H190.646V25.483H205.847ZM194.831 8.257C194.831 10.309 193.265 11.902 191.213 11.902C189.107 11.902 187.595 10.309 187.595 8.257C187.595 6.205 189.107 4.693 191.213 4.693C193.265 4.693 194.831 6.205 194.831 8.257ZM197.801 8.284C197.801 7.771 197.72 7.231 197.612 6.745H202.472V9.661H197.666C197.747 9.229 197.801 8.77 197.801 8.284ZM222.126 2.101H209.382V4.747H219.129C219.21 6.664 218.994 10.255 218.562 12.442L221.397 12.82C222.045 10.417 222.126 6.232 222.126 4.018V2.101ZM223.827 14.845C222.369 15.007 220.101 15.196 217.806 15.358C217.05 15.412 216.321 15.439 215.646 15.466V8.824H212.649V15.574C212.298 15.547 211.92 15.547 211.542 15.574C210.165 15.574 208.842 15.547 207.87 15.547L208.194 18.22H210.084C212.109 18.22 214.269 18.166 216.105 18.058C218.751 17.923 221.991 17.545 224.016 17.248L223.827 14.845ZM232.17 11.308V8.797H228.363V0.723999H225.366V23.242H228.363V11.308H232.17ZM22.787 58.164V55.599H0.053V58.164H22.787ZM16.361 42.747V44.475C16.361 47.715 16.01 51.252 15.497 53.439L18.44 53.763C19.007 50.82 19.304 47.283 19.385 43.422V40.182H3.374V42.747H16.361ZM25.1879 54.789C29.9939 55.005 35.3129 54.762 39.2279 54.06L38.9309 51.522C35.7449 52.17 31.1819 52.251 28.2119 52.116V42.801H36.6899V40.236H25.1879V54.789ZM43.8989 38.724H40.8749V61.215H43.8989V48.606H48.0029V46.041H43.8989V38.724ZM64.0388 44.178C64.0388 45.933 62.4188 47.256 60.0158 47.256C57.5318 47.256 55.9658 45.933 55.9658 44.178C55.9658 42.315 57.6398 41.073 60.0158 41.073C62.3648 41.073 64.0388 42.315 64.0388 44.178ZM67.0628 44.178C67.0628 40.803 64.0118 38.535 60.0158 38.535C56.1008 38.535 52.9418 40.776 52.9418 44.178C52.9418 47.769 56.2628 49.713 60.0158 49.713C63.6878 49.713 67.0628 47.742 67.0628 44.178ZM64.7138 61.782H55.3718V57.867H64.7138V61.782ZM67.6838 64.347V55.356H52.3748V64.347H67.6838ZM71.3828 53.493V50.955H48.6488V53.493H71.3828ZM80.5951 47.121C80.5951 51.252 83.4571 54.033 87.3991 54.033C91.3141 54.033 94.2031 51.252 94.2031 47.121C94.2031 42.99 91.3141 40.182 87.3991 40.182C83.4571 40.182 80.5951 42.99 80.5951 47.121ZM83.6461 47.094C83.6461 44.475 85.2121 42.882 87.3991 42.882C89.5321 42.882 91.1521 44.475 91.1521 47.094C91.1521 49.713 89.5591 51.306 87.3991 51.306C85.2121 51.306 83.6461 49.713 83.6461 47.094ZM97.9831 61.215H100.98V38.724H97.9831V61.215ZM107.377 47.094C107.377 44.421 108.943 42.828 111.103 42.828C113.182 42.828 114.883 44.448 114.883 47.094C114.883 49.767 113.182 51.36 111.103 51.36C108.943 51.36 107.377 49.767 107.377 47.094ZM127.924 51.954V49.335H123.847V45.744H127.924V43.152H123.847V38.724H120.877V61.215H123.847V51.954H127.924ZM104.353 47.121C104.353 51.306 107.188 54.114 111.103 54.114C115.045 54.114 117.907 51.306 117.907 47.121C117.907 42.963 115.045 40.047 111.103 40.047C107.188 40.047 104.353 42.963 104.353 47.121ZM130.568 40.29V42.909H139.154C138.803 47.175 135.266 51.387 129.353 53.547L131.081 56.004C134.618 54.573 137.399 52.548 139.397 49.929C141.395 47.31 142.394 44.097 142.421 40.29H130.568ZM146.525 61.215H149.549V38.724H146.525V61.215Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
